/**
 * Plain Text Builder
 * Converts receipt data to plain text format for raw printing
 * Compatible with QZ Tray plain flavor: https://qz.io/docs/raw#plain
 */

import dayjs from 'dayjs'
import { isEmpty } from 'lodash-es'
import { currency, formatDate } from '../formatting'
import { isOptionEnabled } from '../helper'
import { getOjolDetailForReceipt } from './receiptOjolHelper'
import { getTableDetailForReceipt } from './receiptTableHelper'
import { getServedByNamesForReceipt } from './receiptServedByHelper'
import { getReceiptContact } from './receiptContactHelper'
import { ESCPOS } from './escposBuilder'
import { getReceiptWitholding } from './receiptWitholdingHelper'

// Paper width configurations (in characters)
export const PAPER_WIDTHS = {
  WIDTH_80MM: 44, // 80mm thermal paper
  WIDTH_58MM: 32, // 58mm thermal paper = 32 characters
  WIDTH_DOTMATRIX: 40, // Dot matrix (mis. Epson TM-U220, ~76mm) — nilai aman-konservatif, mudah di-tune
  // Dot matrix continuous form (mis. Epson LX-310) @ 10 CPI = 80 kolom. Pakai 78: baris pas 80
  // memicu auto-wrap printer sehingga `\n` sesudahnya jadi baris kosong ganda.
  WIDTH_DOTMATRIX_A4: 78,
  DEFAULT: 42, // Default/legacy width
}

// Panjang halaman continuous form (baris @ 6 LPI): 9,5" × 11" dan 9,5" × 5,5"
const DOTMATRIX_PAGE_LINES = {
  dotmatrix_a4: 66,
  dotmatrix_half_a4: 33,
}

// Lebar kolom baris item 1-baris untuk dot matrix A4 (lihat createItemRowWide)
const WIDE_ITEM_AMOUNT_MIN_WIDTH = 14
const WIDE_ITEM_MIDDLE_MIN_WIDTH = 24

/**
 * Get paper width based on paper size option
 */
const getPaperWidth = (paperSize) => {
  switch (paperSize) {
    case '80mm':
    case 80:
      return PAPER_WIDTHS.WIDTH_80MM
    case '58mm':
    case 58:
      return PAPER_WIDTHS.WIDTH_58MM
    case 'dotmatrix':
      return PAPER_WIDTHS.WIDTH_DOTMATRIX
    case 'dotmatrix_a4':
    case 'dotmatrix_half_a4':
      return PAPER_WIDTHS.WIDTH_DOTMATRIX_A4
    default:
      return PAPER_WIDTHS.DEFAULT
  }
}

/**
 * Convert any value to string safely
 */
const toStr = (v) => String(v ?? '')

/**
 * Create separator line
 */
export const createLine = (character = '-', width = 42) => {
  return `${character.repeat(width)}\n`
}

/**
 * Force LEFT line (full width)
 * padEnd(width) bikin 1 baris penuh -> aman walaupun printer "lagi center state"
 */
export const createLeftLine = (text = '', width = 42) => {
  const t = toStr(text)
  return `${t.slice(0, width).padEnd(width, ' ')}\n`
}

/**
 * Force RIGHT line (full width)
 */
export const createRightLine = (text = '', width = 42) => {
  const t = toStr(text)
  if (t.length >= width) {
    return `${t.slice(0, width)}\n`
  }
  return `${t.padStart(width, ' ')}\n`
}

/**
 * Force CENTER line (full width)
 */
export const createCenterLine = (text = '', width = 42) => {
  const t = toStr(text)

  if (t.length >= width) {
    return `${t.slice(0, width)}\n`
  }

  const leftSpaces = Math.floor((width - t.length) / 2)
  const centered = `${' '.repeat(leftSpaces)}${t}`

  // padEnd biar full width
  return `${centered.padEnd(width, ' ')}\n`
}

/**
 * Wrap text into multiple lines by width (simple wrap)
 * - Tidak memotong kata secara pintar (simple slicing)
 * - Aman untuk nama item panjang
 */
export const wrapText = (text = '', width = 42) => {
  const t = toStr(text)
  const result = []

  if (!t) {
    return ['']
  }

  let i = 0
  while (i < t.length) {
    result.push(t.slice(i, i + width))
    i += width
  }

  return result
}

/**
 * Word-aware wrap (pecah di spasi) — samakan dengan escposBuilder agar nama
 * produk/alamat/catatan panjang tampil identik di mode plain & thermal.
 */
export const wrapWords = (text = '', width = 42) => {
  const t = String(text ?? '').trim()
  if (!t) {
    return ['']
  }

  const words = t.split(/\s+/)
  const result = []
  let line = ''

  words.forEach((w) => {
    if (!line) {
      line = w
      return
    }

    if (`${line} ${w}`.length <= width) {
      line = `${line} ${w}`
      return
    }

    result.push(line)
    line = w
  })

  if (line) {
    result.push(line)
  }

  return result
}

/**
 * Multiline LEFT text (wrap + full width each line)
 */
export const createLeftMultiline = (text = '', width = 42) => {
  return wrapWords(text, width)
    .map((line) => createLeftLine(line, width))
    .join('')
}

export const createCenterMultiline = (text = '', width = 42) => {
  const t = String(text ?? '')
  if (!t) {
    return createCenterLine('', width)
  }

  return wrapWords(t, width)
    .map((line) => createCenterLine(line, width))
    .join('')
}

/**
 * Create row with left and right aligned text
 * Example:
 *  "2x 5.000" ................ "10.000"
 *
 * Ketika overflow: truncate sisi KANAN (value) agar label kiri tetap utuh.
 * Cocok untuk Pelanggan, Kasir, dll di mana label harus selalu terlihat.
 */
export const createRow = (left, right, width = 42, options = {}) => {
  const { maxRightLength } = options
  const leftStr = toStr(left)
  let rightStr = toStr(right)

  // Batasi panjang sisi kanan - label kiri (Pelanggan, Waktu, dll) tetap utuh
  const availableForRight = Math.max(0, width - leftStr.length - 1)
  const effectiveMaxRight =
    maxRightLength != null ? Math.min(maxRightLength, availableForRight) : availableForRight

  if (rightStr.length > effectiveMaxRight) {
    const ellipsis = '...'
    const truncateAt = Math.max(0, effectiveMaxRight - ellipsis.length)
    rightStr = rightStr.slice(0, truncateAt) + ellipsis
  }

  const spaces = width - leftStr.length - rightStr.length

  if (spaces >= 0) {
    return `${leftStr}${' '.repeat(spaces)}${rightStr}\n`
  }

  const truncatedRight = rightStr.slice(0, Math.max(0, width - leftStr.length - 1))
  return `${leftStr} ${truncatedRight}\n`
}

/**
 * Create row but supports multiline on LEFT part (wrap left)
 * Right stays only on first line.
 *
 * Cocok untuk:
 * - Nama item panjang di kiri
 * - Harga di kanan
 */
export const createRowWrapLeft = (left, right, width = 42) => {
  const leftStr = toStr(left)
  const rightStr = toStr(right)

  // Hitung sisa ruang kiri setelah right
  const rightLen = rightStr.length
  const leftWidthFirstLine = Math.max(10, width - rightLen - 1) // minimal 10 char untuk kiri

  const leftLines = wrapText(leftStr, leftWidthFirstLine)

  let out = ''

  // baris pertama: kiri + kanan
  out += createRow(leftLines[0] ?? '', rightStr, width)

  // baris selanjutnya: kiri saja full width
  for (let i = 1; i < leftLines.length; i += 1) {
    out += createLeftLine(leftLines[i], width)
  }

  return out
}

/**
 * Baris item 1-baris untuk kertas lebar (dot matrix A4):
 * [nama (wrap)] [qty x harga (rata kanan)] [jumlah (rata kanan)]
 * Nama yang panjang lanjut ke baris berikutnya di kolom nama saja.
 */
export const createItemRowWide = (name, middle, amount, width = 78) => {
  const middleStr = toStr(middle)
  const amountStr = toStr(amount)
  // Kolom tengah & jumlah melebar bila isinya panjang; kolom nama yang mengalah
  const middleWidth = Math.max(WIDE_ITEM_MIDDLE_MIN_WIDTH, middleStr.length)
  const amountWidth = Math.max(WIDE_ITEM_AMOUNT_MIN_WIDTH, amountStr.length)
  const nameWidth = Math.max(10, width - middleWidth - amountWidth - 2)

  const nameLines = wrapWords(name, nameWidth).flatMap((line) => wrapText(line, nameWidth))

  const firstLine = `${nameLines[0].padEnd(nameWidth)} ${middleStr.padStart(middleWidth)} ${amountStr.padStart(amountWidth)}`
  const restLines = nameLines.slice(1).map((line) => `${line}\n`)

  return [`${firstLine.trimEnd()}\n`, ...restLines].join('')
}

/**
 * Format quantity (absolute value)
 */
const formatQty = (qty) => {
  return Math.abs(qty)
}

/**
 * Ambil nama satuan item dari daftar units (redux). Samakan dengan escposBuilder.
 */
const getUnitName = (units, item) => {
  if (!Array.isArray(units) || units.length === 0) {
    return ''
  }
  const unitId = item.unit_id ?? item.product?.unit_id
  const unit = units.find((u) => String(u.id) === String(unitId))
  return unit?.name ?? ''
}

/**
 * Cetak baris ekstra (modifier) per item — versi plain text.
 * withPrice=true (struk/bill) → harga di kanan; selain itu (kitchen/checker) nama saja.
 */
const pushModifierLines = (lines, modifiers, width, options = {}) => {
  const { withPrice = false } = options
  const mods = Array.isArray(modifiers) ? modifiers : []
  mods.forEach((m) => {
    const mQty = Number(m.qty) || 1
    const label = `  + ${m.option_name || '-'}${mQty > 1 ? ` x${mQty}` : ''}`
    const price = Number(m.price) || 0
    if (withPrice && price > 0) {
      lines.push(createRow(label, currency(price * mQty), width))
    } else {
      lines.push(createLeftMultiline(label, width))
    }
  })
}

/**
 * Build plain text commands for receipt
 * Format: Plain text without ESC/POS control characters
 */
export const buildReceiptCommands = (data, options = {}, formatMessage) => {
  const {
    printerFunction = 'struk',
    outlet = {},
    companyOptions = {},
    outletOptions = {},
    paperWidth, // '80mm', '58mm', or number (default: 42)
    units = [], // Daftar satuan dari redux untuk menampilkan nama satuan di struk
    enableCut = false, // Paper cut hanya jika auto_cut aktif (lihat akhir fungsi)
    // Note: enableOpenCashDrawer is not used in plain text mode
    // enableOpenCashDrawer = false,
  } = options

  // Get paper width (default: 42, or from paperWidth option)
  const width = paperWidth ? getPaperWidth(paperWidth) : PAPER_WIDTHS.DEFAULT

  // Dot matrix continuous form (ESC/P): atur panjang halaman + form feed di akhir, item 1 baris
  const pageLines = DOTMATRIX_PAGE_LINES[paperWidth]
  const isWideDotMatrix = Boolean(pageLines)

  const lines = []

  // ESC C n — panjang halaman n baris; posisi kertas saat ini jadi awal halaman (top-of-form)
  if (isWideDotMatrix) {
    lines.push(`\x1B\x43${String.fromCharCode(pageLines)}`)
  }

  // Header section
  if (printerFunction === 'struk' || printerFunction === 'bill') {
    // Header teks kustom
    if (
      isOptionEnabled(outletOptions?.print_style_show_header) &&
      !isEmpty(outletOptions.print_style_header)
    ) {
      lines.push(createCenterMultiline(outletOptions.print_style_header, width))
    }
    // Outlet name
    if (isOptionEnabled(outletOptions?.print_style_show_outlet_name) && !isEmpty(outlet?.name)) {
      lines.push(createCenterLine(outlet.name, width))
    }
    // Company name
    if (
      isOptionEnabled(outletOptions?.print_style_show_company_name) &&
      !isEmpty(companyOptions?.property_name)
    ) {
      lines.push(createCenterLine(companyOptions.property_name, width))
    }
    const receiptContact = getReceiptContact(outlet, companyOptions)
    // Alamat (prioritas outlet, fallback perusahaan)
    if (
      isOptionEnabled(outletOptions?.print_style_show_address) &&
      !isEmpty(receiptContact.address)
    ) {
      lines.push(createCenterMultiline(receiptContact.address, width))
    }
    // Email
    if (
      isOptionEnabled(outletOptions?.print_style_show_email) &&
      !isEmpty(companyOptions?.property_email)
    ) {
      lines.push(createCenterLine(companyOptions.property_email, width))
    }
    // Telepon (prioritas outlet, fallback perusahaan)
    if (isOptionEnabled(outletOptions?.print_style_show_phone) && !isEmpty(receiptContact.phone)) {
      lines.push(createCenterLine(receiptContact.phone, width))
    }
  } else if (printerFunction === 'checker') {
    lines.push(createCenterLine('CHECKER', width))
  } else if (printerFunction === 'kitchen') {
    lines.push(createCenterLine(data.title || formatMessage({ id: 'kitchen' }), width))
  }
  lines.push('\n')

  // Order details section - ensure left alignment
  lines.push(createLine('-', width))
  if (data.queue_number_formatted) {
    lines.push(createRow(formatMessage({ id: 'queue_number' }), data.queue_number_formatted, width))
  }
  if (isOptionEnabled(outletOptions.print_style_show_order_number)) {
    lines.push(createRow(formatMessage({ id: 'order_number' }), data.ref_number || '-', width))
  }
  lines.push(
    createRow(
      formatMessage({ id: 'time' }),
      formatDate(data.log?.action?.created_at, 'DD MMMM YYYY HH:mm'),
      width,
    ),
  )
  lines.push(
    createRow(formatMessage({ id: 'report.cashier' }), data.log?.action?.user?.name || '-', width),
  )
  if (isOptionEnabled(outletOptions.print_style_show_customer)) {
    lines.push(
      createRow(formatMessage({ id: 'financeOrder.customer' }), data.contact?.name || '-', width, {
        maxRightLength: 25,
      }),
    )
  }
  if (outletOptions.print_style_show_order_type === '1') {
    lines.push(
      createRow(formatMessage({ id: 'report.order_type' }), data.invoice_type?.name || '-', width),
    )
  }

  const ojolDetail = getOjolDetailForReceipt(data)
  if (ojolDetail) {
    const providerLabel = ojolDetail.provider_name || ojolDetail.courier
    if (providerLabel) {
      lines.push(
        createRow(formatMessage({ id: 'cashier.receipt_ojol_name' }), providerLabel, width, {
          maxRightLength: 22,
        }),
      )
    }
    if (ojolDetail.order_name) {
      lines.push(
        createRow(
          formatMessage({ id: 'cashier.receipt_ojol_order_no' }),
          String(ojolDetail.order_name),
          width,
          { maxRightLength: 22 },
        ),
      )
    }
  }

  // Pegawai "Dilayani Oleh" — ikut aturan show/hide jenis transaksi
  const servedByNames = getServedByNamesForReceipt(data)
  if (servedByNames && outletOptions.print_style_show_order_type === '1') {
    lines.push(
      createRow(formatMessage({ id: 'cashier.receipt_served_by' }), servedByNames, width, {
        maxRightLength: 22,
      }),
    )
  }

  // Detail meja — ikut aturan show/hide jenis transaksi (print_style_show_order_type)
  const tableDetail = getTableDetailForReceipt(data)
  if (tableDetail && outletOptions.print_style_show_order_type === '1') {
    const tableName =
      tableDetail.table_name ||
      formatMessage({ id: 'cashier.table_detail.table_fallback' }, { id: tableDetail.table_id })
    lines.push(
      createRow(formatMessage({ id: 'cashier.receipt_table' }), tableName, width, {
        maxRightLength: 22,
      }),
    )
    if (tableDetail.order_name) {
      lines.push(
        createRow(
          formatMessage({ id: 'cashier.receipt_table_order_name' }),
          String(tableDetail.order_name),
          width,
          { maxRightLength: 22 },
        ),
      )
    }
    if (tableDetail.reserved_count) {
      lines.push(
        createRow(
          formatMessage({ id: 'cashier.receipt_table_guests' }),
          String(tableDetail.reserved_count),
          width,
        ),
      )
    }
  }

  lines.push(createLine('-', width))

  // Bawaan aktif: hanya '0' yang menyembunyikan deskripsi item
  const showItemDescription = outletOptions.print_style_show_product_description?.toString() !== '0'

  const witholding = getReceiptWitholding(
    data,
    outletOptions,
    formatMessage({ id: 'financeInvoices.witholding' }),
  )

  if (printerFunction === 'struk' || printerFunction === 'bill') {
    const hideProductLinePrices = outletOptions.print_style_show_product_price?.toString() === '0'
    const itemsLength = data.items?.length || 0
    // Struk/Bill format - with prices (or qty only when print_style_show_product_price is off)
    // Ensure left alignment for items (plain text doesn't need alignment commands, just ensure no centering)
    data.items?.forEach((item) => {
      // Product name (left aligned - plain text is left by default)
      const productName = item.custom_product_name || item.product?.name || '-'
      const unitName = getUnitName(units, item)
      if (hideProductLinePrices) {
        // Qty [satuan] tanpa harga
        const qtyLine = unitName ? `${formatQty(item.qty)} ${unitName}` : `${formatQty(item.qty)}x`
        if (isWideDotMatrix) {
          lines.push(createItemRowWide(productName, qtyLine, '', width))
        } else {
          lines.push(createLeftMultiline(productName, width))
          lines.push(createLeftLine(qtyLine, width))
        }
      } else {
        // Qty [satuan] x Price = Amount
        const qtyPrice = unitName
          ? `${formatQty(item.qty)} ${unitName} x ${currency(item.price)}`
          : `${formatQty(item.qty)}x ${currency(item.price)}`
        const amount = currency(item.amount || 0)
        if (isWideDotMatrix) {
          lines.push(createItemRowWide(productName, qtyPrice, amount, width))
        } else {
          lines.push(createLeftMultiline(productName, width))
          lines.push(createRow(qtyPrice, amount, width))
        }

        // Discount if any
        if (item.discount_amount > 0) {
          // Baris hadiah BOGO: tampilkan "Gratis - {nama promo}", bukan "Diskon per item"
          const isReward = Number(item.price_rule_reward_id) > 0
          const rewardName = item.price_rule?.name || item.price_rule_reward?.rule_name || ''
          let discountLabel
          if (isReward) {
            discountLabel = rewardName
              ? `${formatMessage({ id: 'priceRule.free' })} - ${rewardName}`
              : formatMessage({ id: 'priceRule.free' })
          } else {
            discountLabel =
              item.price_rule?.name ||
              formatMessage({ id: 'financePurchaseOrders.discount_per_item' })
          }
          const discountAmount = `(${currency(item.discount_amount)})`
          lines.push(createRow(discountLabel, discountAmount, width))
        }
      }

      // Ekstra (modifier) per item — harga di kanan kecuali harga produk disembunyikan
      pushModifierLines(lines, item.modifiers, width, { withPrice: !hideProductLinePrices })

      // Description/Note (left aligned)
      const lineDesc = item.desc ?? item.description
      if (showItemDescription && lineDesc) {
        lines.push(createLeftMultiline(`> ${lineDesc}`, width))
      }
      // Dot matrix A4: tanpa baris kosong antar item agar ½ A4 muat lebih banyak item
      if (itemsLength > 1 && !isWideDotMatrix) {
        lines.push('\n')
      }
    })
    lines.push(createLine('-', width))

    // Subtotal
    const subtotalLabel = `Subtotal ${data.items?.length || 0} produk`
    lines.push(createRow(subtotalLabel, currency(data.amount || 0), width))

    // Discount per item
    if (data.discount_per_item > 0) {
      lines.push(
        createRow(
          formatMessage({ id: 'financePurchaseOrders.discount_per_item' }),
          `(${currency(data.discount_per_item)})`,
          width,
        ),
      )
    }

    // Additional discounts (array)
    if (!isEmpty(data.additional_discounts) && Array.isArray(data.additional_discounts)) {
      data.additional_discounts.forEach((discount) => {
        const percentLabel =
          discount.discount_type === 1 // PERCENT
            ? ` (${parseFloat(discount.additional_discount_percent || 0)}%)`
            : ''
        const discountLabel =
          formatMessage({ id: 'financePurchaseOrders.additional_discount' }) + percentLabel
        lines.push(
          createRow(
            discountLabel,
            `(${currency(discount.additional_discount_amount || 0)})`,
            width,
          ),
        )
      })
    }

    // Tax
    if (!isEmpty(data.taxes)) {
      Object.keys(data.taxes).forEach((key) => {
        const taxLabel = `${key} (${data.taxes[key].percent}%)`
        const { value } = data.taxes[key]
        const _value = value > 0 ? currency(value) : `(${currency(Math.abs(value))})`
        lines.push(createRow(taxLabel, _value, width))
      })
    }

    // Biaya pengiriman shipping cost
    if (data.shipping_cost > 0) {
      lines.push(
        createRow(
          formatMessage({ id: 'shipping.shipping_cost' }),
          currency(data.shipping_cost),
          width,
        ),
      )
    }

    // Fees
    if (!isEmpty(data.fees) && Array.isArray(data.fees)) {
      data.fees.forEach((fee) => {
        lines.push(createRow(fee.name || '-', currency(fee.value || 0), width))
      })
    }

    // Additional fees
    if (data.totalMdrFee > 0) {
      lines.push(
        createRow(formatMessage({ id: 'additional_fee' }), currency(data.totalMdrFee), width),
      )
    }

    // Pemotongan (bisa disembunyikan per outlet)
    witholding.rows.forEach((row) => {
      lines.push(createRow(row.label, currency(row.amount), width))
    })

    // Total
    lines.push(createRow('Total', currency(witholding.amountAfterTax), width))
  } else if (printerFunction === 'checker' || printerFunction === 'kitchen') {
    // Kitchen/Checker format - no prices, qty [satuan] in front
    data.items?.forEach((item) => {
      const unitName = getUnitName(units, item)
      const qtyStr = unitName ? `${formatQty(item.qty)} ${unitName}` : formatQty(item.qty)
      lines.push(
        createLeftMultiline(`(${qtyStr}) ${item.custom_product_name || item.product?.name}`, width),
      )
      // Ekstra (modifier) per item — tanpa harga (untuk dapur/checker)
      pushModifierLines(lines, item.modifiers, width, { withPrice: false })
      // Description/Note
      const lineDesc = item.desc ?? item.description
      if (showItemDescription && lineDesc) {
        lines.push(createLeftMultiline(`> ${lineDesc}`, width))
        lines.push('\n')
      }
    })
    lines.push('\n')
  }

  if (printerFunction === 'struk') {
    lines.push(createLine('-', width))
    data.payments?.forEach((payment, index) => {
      let amountAfterTax = payment.amount_after_tax
      const payback = payment.payback || 0
      if (payback > 0) {
        amountAfterTax = payment.amount_after_tax + payback
      }
      amountAfterTax = witholding.getPaymentAmount(amountAfterTax, index, data.payments.length)
      lines.push(
        createRow(
          `${payment.payment_type?.name || '-'} ${
            (payment.payment_fee || 0) > 0 ? `+ ${formatMessage({ id: 'additional_fee' })}` : ''
          }`,
          currency(amountAfterTax),
          width,
        ),
      )
    })
    if (data.payment_amount > 0) {
      lines.push(
        createRow(
          formatMessage({ id: 'report.total_payment' }),
          currency((Number(data.payment_amount) || 0) + witholding.addBack),
          width,
        ),
      )
    }
    if (data.payment_back_amount > 0) {
      lines.push(
        createRow(
          formatMessage({ id: 'report.return' }),
          currency(data.payment_back_amount),
          width,
        ),
      )
    }
    lines.push(createLine('-', width))
  }

  // Footer section
  if (printerFunction === 'struk' || printerFunction === 'bill') {
    // Note
    if (isOptionEnabled(outletOptions.print_style_show_note) && outletOptions.print_style_note) {
      lines.push(createCenterMultiline(outletOptions.print_style_note, width))
    }

    // Footer
    if (
      isOptionEnabled(outletOptions.print_style_show_footer) &&
      outletOptions.print_style_footer
    ) {
      lines.push(createCenterMultiline(outletOptions.print_style_footer, width))
    }
  }

  // Jumlah cetak struk (setting lokal aktif) — optimistic: cetakan saat ini = count BE + 1
  if (options.printReceiptCountActive && printerFunction === 'struk') {
    const printedNow = (Number(data.print_receipt_count) || 0) + 1
    lines.push(createCenterLine(`Dicetak ${printedNow} kali`, width))
  }

  // Timestamp
  lines.push(createCenterLine(`--- ${formatDate(dayjs())} ---`, width))

  // Dot matrix continuous form: form feed → kertas maju ke awal halaman berikutnya (perforasi),
  // tanpa feed manual & tanpa paper cut
  if (isWideDotMatrix) {
    lines.push('\x0C')
    return lines
  }

  // Feed lines before cut (represented as empty lines in plain text)
  for (let i = 0; i < 8; i += 1) {
    lines.push('\n')
  }

  // Paper cut (GS V 0) hanya dikirim jika auto_cut aktif — mode plain juga dipakai
  // printer dot matrix yang tidak mengenal perintah GS V
  if (enableCut) {
    lines.push(ESCPOS.CUT_PAPER_FULL)
  }
  // Cash drawer is handled by printer hardware, not needed in plain text output

  // Convert to QZ Tray plain format
  // Format sesuai dokumentasi: https://qz.io/docs/raw#plain
  // Return langsung array of strings seperti ESC/POS builder
  return lines
}

export default {
  buildReceiptCommands,
}
